import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormArray, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import _ from 'lodash';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { isMmlProcessName, isUdProcessName, getDisplayUnit } from 'src/app/shared/utils/utils';

@Component({
    selector: 'app-modal-quantity-produced',
    standalone: true,
    imports: [
        NzModalTitleDirective,
        NzModalFooterDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzMessageModule,
        NzFormModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './modal-quantity-produced.component.html',
    styleUrl: './modal-quantity-produced.component.scss'
})
export class ModalQuantityProducedComponent implements OnInit {
    @Output() emitService = new EventEmitter();

    data = inject(NZ_MODAL_DATA);
    currentUser: UserDto = new UserDto();
    finalProducts: FinalProductDto[] = [];
    productionProcessName!: string;
    validateForm: FormGroup;
    isLoading: boolean = true;
    isSaving: boolean = false;

    readonly userLoggedService = inject(UserLoggedService);
    readonly message = inject(NzMessageService);
    readonly fb = inject(NonNullableFormBuilder);
    private readonly modalRef = inject(NzModalRef);

    /**
     * Devuelve la unidad de medida a mostrar según el proceso de producción
     */
    getDisplayUnit(): string {
        return getDisplayUnit(undefined, this.productionProcessName, this.finalProducts?.[0]?.article?.measurementUnit?.abbreviation || '');
    }

    isKgProcess(): boolean {
        return isMmlProcessName(this.productionProcessName);
    }

    constructor() {
        this.validateForm = this.fb.group({
            quantities: this.fb.array([])
        });
    }

    get quantities(): FormArray {
        return this.validateForm.get('quantities') as FormArray;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.finalProducts = this.data.finalProducts;
        this.productionProcessName = this.data.productionProcessName;

        if (!this.productionProcessName) {
            this.message.error('No se ha proporcionado el nombre del proceso de producción');
            this.modalRef.close();
            return;
        }

        this.finalProducts.forEach(finalProduct => {
            this.quantities.push(this.fb.group({
                quantity: [finalProduct.quantityProduced, [Validators.required, Validators.min(0)]],
                axuliaryQuantity: [finalProduct.axuliaryQuantityProduced ?? 0, [Validators.min(0)]]
            }));
        });
        this.isLoading = false;
    }

    async submitForm(): Promise<void> {
        try {
            this.isSaving = true;

            if (!this.validateForm.valid) {
                this.isSaving = false;
                this.message.error(`Por favor, rellene los campos obligatorios`);
                return;
            }

            const useKgInput = this.isKgProcess();

            const edittedFinalProducts: FinalProductDto[] = this.quantities.value.map((control: any, index: number) => ({
                ...this.finalProducts[index],
                quantityProduced: control.quantity,
                axuliaryQuantityProduced: control.axuliaryQuantity ?? undefined
            }));

            if (edittedFinalProducts.some(fp => fp.quantityProduced! < 0)) {
                this.isSaving = false;
                throw new Error(`La cantidad producida no puede ser negativa`);
            }

            if (useKgInput && edittedFinalProducts.some(fp => (fp.axuliaryQuantityProduced ?? 0) < 0)) {
                this.isSaving = false;
                throw new Error(`La cantidad en kg no puede ser negativa`);
            }

            this.isSaving = false;
            this.emitService.emit(edittedFinalProducts);
            this.modalRef.close();
        } catch (e: unknown) {
            this.isSaving = false;
            if (e instanceof Error) {
                this.message.error(e.message);
            }
        }
    }

    handleCancel() {
        this.modalRef.close();
    }
}
